<template>
  <div>
    <div class="flex-col sm:grid sm:grid-cols-4 overflow-hidden hidden">
      <div class="w-full sm:col-span-1"></div>
      <div
        class="w-full sm:col-span-3 rounded-lg sm:rounded-l-none sm:rounded-r-lg flex flex-col pt-5 px-3 sm:px-0 sm:pt-0"
      >
        <div class="items-center justify-center my-2 sm:flex">
          <portal-billing-company-logo />
        </div>
      </div>
    </div>
    <div
      v-if="acceptTermsError"
      class="w-full h-12 rounded-lg bg-red-600 text-white flex items-center p-5 my-3 justify-between"
    >
      <div>
        <ion-icon
          name="warning-outline"
          class="mr-3"
        ></ion-icon>
        Unable to sign proposal. Please refresh and try again.
      </div>
      <button
        type="button"
        @click.prevent="acceptTermsError = false"
      >
        <ion-icon name="close-outline"></ion-icon>
      </button>
    </div>
    <div class="flex flex-col sm:grid sm:grid-cols-4 overflow-hidden">
      <!-- Left sidebar - full width on mobile, 1/4 on desktop -->
      <div class="w-full sm:col-span-1">
        <div class="h-auto sm:h-full overflow-y-auto border-none">
          <portal-billing-proposal-steps
            :current-step="currentStep"
            :available-steps="availableSteps"
          />
        </div>
      </div>
      <div class="flex items-center justify-center my-2 sm:hidden">
        <portal-billing-company-logo />
      </div>
      <div
        class="w-full sm:col-span-3 rounded-lg sm:rounded-l-none sm:rounded-r-lg flex flex-col sm:h-[80vh] sm:max-h-[750px] h-[70vh] max-h-[700px] px-3 sm:px-0 sm:pt-0"
      >
        <div class="flex flex-col h-full relative">
          <div
            class="absolute inset-0 overflow-y-auto rounded-t-lg bg-white"
            ref="scrollableContainer"
            @scroll="checkScroll"
          >
            <template v-if="!isComplete">
              <portal-billing-proposal-intro v-if="isStepActive('Introduction')" />
              <portal-billing-proposal-packages
                v-if="isStepActive('Packages')"
                :selected-package="selectedPackage"
                @set-selected-package="setSelectedPackage"
              />
              <portal-billing-proposal-services
                v-if="isStepActive('Review Services')"
                :selected-add-ons="termsFormModel.optional_ids"
                @set-selected-add-ons="setSelectedAddOns"
                :selected-package="selectedPackage"
              />
              <portal-billing-proposal-payment
                v-if="isStepActive('Payment')"
                ref="payment"
                :is-payment-optional="isPaymentOptional"
                @next-step="nextStep"
                @submit-loading="paymentSubmitLoading = $event"
                @form-valid="paymentFormValid = $event"
                @set-provider="provider = $event"
                @enable-continue-button="disableContinueButton = false"
                @disable-continue-button="disableContinueButton = true"
              />
              <portal-billing-proposal-terms
                v-if="isStepActive('Terms')"
                :form-model="termsFormModel"
                @update-terms-form-model="termsFormModel = $event"
              />
              <div
                class="h-4 sticky bottom-3"
                v-if="isStepActive('Packages') || isStepActive('Review Services')"
              >
                <div
                  v-if="showScrollButton && isStepActive('Review Services')"
                  @click.prevent="scrollToBottom"
                  class="sticky flex items-center justify-center bottom-3 mx-auto hover:bg-gray-100 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl cursor-pointer w-6 h-6 z-10 ring-1 ring-gray-500 transition-all duration-200 ease-in-out animate-[bounce_1.5s_infinite]"
                >
                  <ion-icon
                    name="arrow-down-outline"
                    class="w-5 h-5 text-gray-500"
                  ></ion-icon>
                </div>
              </div>
            </template>

            <portal-billing-proposal-complete v-else />
          </div>
        </div>
        <!-- Fixed Footer -->
        <div class="rounded-b-lg bg-white">
          <portal-billing-proposal-footer
            v-if="!isComplete"
            :max-steps="maxSteps"
            :current-step="currentStep"
            :current-step-name="getCurrentStepName"
            @accept-proposal="acceptProposal"
            @next-step="nextStep"
            @previous-step="previousStep"
            @payment-step-continue-click="handlePaymentStepContinueClick"
            @skip-payment-step="nextStep"
            :disable-accept-terms-button="disableAcceptTermsButton"
            :disable-continue-button="disableContinueButton"
            :on-payment-step="onPaymentStep"
            :is-payment-optional="isPaymentOptional"
            :payment-submit-loading="paymentSubmitLoading"
            :has-overflow="hasOverflow"
            :has-scrolled-to-bottom="hasScrolledToBottom"
            :selected-package="selectedPackage"
          />
        </div>
      </div>
    </div>
    <!--    <portal-billing-proposal-firm-info-mobile class="sm:hidden"/>-->
  </div>
</template>

<script>
import PortalBillingProposalSteps from "../components/proposal/PortalBillingProposalSteps.vue"
import PortalBillingProposalIntro from "../components/proposal/PortalBillingProposalIntro.vue"
import PortalBillingProposalServices from "../components/proposal/PortalBillingProposalServices.vue"
import PortalBillingProposalTerms from "../components/proposal/PortalBillingProposalTerms.vue"
import PortalBillingProposalPayment from "../components/proposal/PortalBillingProposalPayment.vue"
import PortalBillingProposalComplete from "../components/proposal/PortalBillingProposalComplete.vue"
import PortalBillingProposalFooter from "../components/proposal/PortalBillingProposalFooter.vue"
import Proposal from "../repository/Proposal"
import PortalBillingProposalPackages from "../components/proposal/PortalBillingProposalPackages.vue"
import PortalBillingCompanyLogo from "../components/ui/PortalBillingCompanyLogo.vue"

export default {
  name: "PortalProposals",
  components: {
    PortalBillingCompanyLogo,
    PortalBillingProposalPackages,
    PortalBillingProposalFooter,
    PortalBillingProposalComplete,
    PortalBillingProposalPayment,
    PortalBillingProposalTerms,
    PortalBillingProposalServices,
    PortalBillingProposalIntro,
    PortalBillingProposalSteps,
  },

  data() {
    return {
      currentStep: 1,
      requirePaymentMethod: window.FinancialCents.proposal.require_payment_details,
      paymentFormValid: false,
      isComplete: false,
      proposal: window.FinancialCents.proposal,
      disableAcceptTermsButton: false,
      disableContinueButton: false,
      termsFormModel: {
        accept_terms: false,
        signature: "",
        package_id: window.FinancialCents.proposal.packages[0].id,
        optional_ids: [],
      },
      acceptTermsError: false,
      paymentSubmitLoading: false,
      hasOverflow: false,
      resizeObserver: null,
      contentObserver: null,
      isScrolledToBottom: true,
      hasScrolledToBottom: false,
      selectedPackage: null,
      provider: "",
    }
  },
  watch: {
    termsFormModel: {
      handler(newValue) {
        this.disableAcceptTermsButton = !(newValue.accept_terms && newValue.signature)
      },
      deep: true,
    },
    paymentFormValid() {
      this.disableContinueButton = !this.paymentFormValid
    },
    onPaymentStep(isOnPaymentStep) {
      if (!isOnPaymentStep) {
        this.paymentSubmitLoading = false
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.initializeObservers()
      this.checkOverflow()
      this.checkScroll()
    })
    this.checkForSelectedPackage()
  },

  unmounted() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.contentObserver) {
      this.contentObserver.disconnect()
      this.contentObserver = null
    }
  },

  computed: {
    showScrollButton() {
      return this.hasOverflow && !this.isScrolledToBottom
    },
    maxSteps() {
      return this.availableSteps.length
    },

    hasPackagesStep() {
      return this.proposal.packages.length > 1
    },

    isStepActive() {
      return (stepName) => {
        const currentStepObj = this.availableSteps.find((step) => step.id === this.currentStep)
        return currentStepObj?.name === stepName
      }
    },

    getCurrentStepName() {
      const currentStepObj = this.availableSteps.find((step) => step.id === this.currentStep)
      return currentStepObj?.name
    },

    isProposalSigned() {
      return this.proposal.contacts.some((contact) => contact.signed_at !== null)
    },

    onPaymentStep() {
      return this.isStepActive("Payment")
    },

    isPaymentOptional() {
      return !this.requirePaymentMethod
    },

    showTermsStep() {
      return this.isStepActive("Terms")
    },

    availableSteps() {
      const steps = [{ id: 1, name: "Introduction" }]

      let currentId = 2

      if (this.hasPackagesStep) {
        steps.push({
          id: currentId++,
          name: "Packages",
        })
      }

      steps.push({
        id: currentId++,
        name: "Review Services",
      })

      if (!this.isProposalSigned) {
        steps.push({
          id: currentId++,
          name: "Payment",
          isOptional: !this.requirePaymentMethod,
        })
      }

      steps.push({
        id: currentId,
        name: "Terms",
      })

      return steps
    },
  },

  methods: {
    checkForSelectedPackage() {
      const selectedPackage = this.proposal.packages.find((pkg) => pkg.selected)
      if (selectedPackage) {
        this.setSelectedPackage(selectedPackage)
      }
    },

    setSelectedPackage(pkg) {
      // need to set the selected package if already selected
      this.selectedPackage = pkg
      this.termsFormModel.package_id = pkg.id
    },

    setSelectedAddOns(addOnIds) {
      this.termsFormModel.optional_ids = addOnIds
    },

    initializeObservers() {
      // Observe size changes
      this.resizeObserver = new ResizeObserver(() => {
        this.$nextTick(() => {
          this.checkOverflow()
          this.checkScroll()
        })
      })
      this.resizeObserver.observe(this.$refs.scrollableContainer)

      // Observe content changes
      this.contentObserver = new MutationObserver(() => {
        this.$nextTick(() => {
          this.checkOverflow()
          this.checkScroll()
        })
      })
      this.contentObserver.observe(this.$refs.scrollableContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    },

    checkOverflow() {
      const container = this.$refs.scrollableContainer
      if (!container) {
        return
      }

      // More precise calculation
      const hasVerticalOverflow = Math.abs(container.scrollHeight - container.clientHeight) > 1

      // Double-check with element's computed style
      const computedStyle = window.getComputedStyle(container)
      const isScrollable = computedStyle.overflowY === "auto" || computedStyle.overflowY === "scroll"

      this.hasOverflow = hasVerticalOverflow && isScrollable
      if (!this.hasOverflow && this.isStepActive("Review Services")) {
        this.hasScrolledToBottom = true
      }
    },
    scrollToBottom() {
      const container = this.$refs.scrollableContainer
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      })
    },
    checkScroll() {
      const container = this.$refs.scrollableContainer
      // Consider "at bottom" when within 1px of the bottom to account for rounding
      this.isScrolledToBottom = Math.abs(container.scrollHeight - container.clientHeight - container.scrollTop) <= 200
      if (this.isScrolledToBottom && this.isStepActive("Review Services")) {
        this.hasScrolledToBottom = true
      }
    },

    handlePaymentStepContinueClick() {
      this.$refs.payment.submit()
    },

    nextStep() {
      if (this.currentStep < this.maxSteps) {
        this.currentStep++
      }
      if (this.showTermsStep) {
        this.disableAcceptTermsButton = true
      }
      this.$refs.scrollableContainer.scrollTop = 0
    },

    previousStep() {
      //clear add ons selection if user goes back to select a package
      if (this.isStepActive("Review Services")) {
        this.termsFormModel.optional_ids = []
      }

      if (this.currentStep > 1) {
        this.currentStep--
      }

      if (this.showTermsStep) {
        this.disableAcceptTermsButton = true
      }
      this.$refs.scrollableContainer.scrollTop = 0
      this.paymentFormValid = false
    },

    async acceptProposal() {
      this.disableAcceptTermsButton = true
      try {
        await Proposal.sign(this.proposal.id, this.termsFormModel)
        this.isComplete = true
        this.currentStep = 10
      } catch (e) {
        this.disableAcceptTermsButton = false
        this.acceptTermsError = true
        console.error(e)
      }
    },
  },
}
</script>
